import React, { useEffect, useState, useMemo } from 'react';
import { compose } from 'redux-starter-kit';
import { connect } from 'react-redux';
import Store from 'store/store';
import Grid from '@material-ui/core/Grid';
import { Modal, BoxWrapper, Button } from 'components/Base';
import { makeStyles, CircularProgress } from '@material-ui/core';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import api from 'api';
import { MRT_ColumnDef } from 'material-react-table';
import {
  resetTableState,
  getPercentageObject,
  getStringObject,
  getCachableData
} from './utils';
import FactSheetLink from 'components/FactSheet/FactSheetLink';
import RatioYearFilter from './RatioYearFilter';
import GlossaryLookup from 'components/Homepage/components/Glossary/GlossaryLookup';

const { selectors, actions } = Store;

const useStyles = makeStyles({
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minHeight: '25rem'
  },
  riskScoreWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  link: {
    float: 'right',
    fontWeight: 600,
    color: '#4469a6',
    fontSize: '0.9rem',
    cursor: 'pointer',
    marginRight: '0.6rem'
  }
});

const RatioTabContent = ({
  investments,
  riskRatings,
  riskRatingsCacheIsValid,
  updateRiskRatings,
  selectedInvestment
}: any) => {
  const classes = useStyles();
  const [funds, setFunds] = useState([] as any[]);
  const [modalOpen, setModalOpen] = useState(false);
  const [parentMpName, setParentMpName] = useState('');
  const [parentRowData, setParentRowData] = useState([] as any[]);
  const [rowData, setRowData] = useState([] as any[]);
  const [modelPortfolioFunds, setModelPortfolioFunds] = useState([] as any[]);
  const [nestedModelPortfolios, setNestedModelPortfolios] = useState(
    [] as any[]
  );

  const [selectedMpName, setSelectedMpName] = useState('');
  const [isLoading, setIsLoading] = useState(!riskRatings.investments.length);

  const [count, setCount] = useState(0);

  useEffect(() => {
    setFunds(investments.filter((investment: any) => investment.is_checked));
    getCachableData(
      investments,
      riskRatingsCacheIsValid,
      setIsLoading,
      () => api.getResearchItemRiskRatings(selectedInvestment),
      updateRiskRatings
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investments]);

  useEffect(() => {
    setModelPortfolioFunds(
      rowData.filter((item: any) => !!item.fund || item.type === 'f')
    );
    setNestedModelPortfolios(
      rowData.filter(
        (item: any) => ('key' in item && !item.fund) || item.type === 'mp'
      )
    );
  }, [rowData]);

  const goBackToParentMp = () => {
    setSelectedMpName(parentMpName);
    setRowData(parentRowData);
    setParentMpName('');
    setParentRowData([]);
  };

  const getRatioObject = (key: string, nullable = false) => {
    return {
      Header: (props: any) => (
        <GlossaryLookup term={props.column.columnDef.header} />
      ),
      ...getPercentageObject(key, nullable)
    };
  };

  const ratioColumns = [
    {
      accessorKey: 'allocation',
      header: '%',
      ...getRatioObject('allocation')
    },
    {
      accessorKey: 'one_y_alpha',
      header: 'Alpha (1Y)',
      ...getRatioObject('one_y_alpha', true)
    },
    {
      accessorKey: 'three_y_alpha',
      header: 'Alpha (3Y)',
      ...getRatioObject('three_y_alpha', true)
    },
    {
      accessorKey: 'five_y_alpha',
      header: 'Alpha (5Y)',
      ...getRatioObject('five_y_alpha', true)
    },
    {
      accessorKey: 'one_y_standard_deviation',
      header: 'Volatility (1Y)',
      ...getRatioObject('one_y_standard_deviation', true)
    },
    {
      accessorKey: 'three_y_standard_deviation',
      header: 'Volatility (3Y)',
      ...getRatioObject('three_y_standard_deviation', true)
    },
    {
      accessorKey: 'five_y_standard_deviation',
      header: 'Volatility (5Y)',
      ...getRatioObject('five_y_standard_deviation', true)
    },
    {
      accessorKey: 'one_y_beta',
      header: 'Beta (1Y)',
      ...getRatioObject('one_y_beta', true)
    },
    {
      accessorKey: 'three_y_beta',
      header: 'Beta (3Y)',
      ...getRatioObject('three_y_beta', true)
    },
    {
      accessorKey: 'five_y_beta',
      header: 'Beta (5Y)',
      ...getRatioObject('five_y_beta', true)
    },
    {
      accessorKey: 'one_y_sharpe_ratio',
      header: 'Sharpe Ratio (1Y)',
      ...getRatioObject('one_y_sharpe_ratio', true)
    },
    {
      accessorKey: 'three_y_sharpe_ratio',
      header: 'Sharpe Ratio (3Y)',
      ...getRatioObject('three_y_sharpe_ratio', true)
    },
    {
      accessorKey: 'five_y_sharpe_ratio',
      header: 'Sharpe Ratio (5Y)',
      ...getRatioObject('five_y_sharpe_ratio', true)
    },
    {
      accessorKey: 'one_y_information_ratio',
      header: 'Information Ratio (1Y)',
      ...getRatioObject('one_y_information_ratio', true)
    },
    {
      accessorKey: 'three_y_information_ratio',
      header: 'Information Ratio (3Y)',
      ...getRatioObject('three_y_information_ratio', true)
    },
    {
      accessorKey: 'five_y_information_ratio',
      header: 'Information Ratio (5Y)',
      ...getRatioObject('five_y_information_ratio', true)
    },
    {
      accessorKey: 'one_y_r_squared',
      header: 'R-Squared Ratio (1Y)',
      ...getRatioObject('one_y_r_squared', true)
    },
    {
      accessorKey: 'three_y_r_squared',
      header: 'R-Squared Ratio (3Y)',
      ...getRatioObject('three_y_r_squared', true)
    },
    {
      accessorKey: 'five_y_r_squared',
      header: 'R-Squared Ratio (5Y)',
      ...getRatioObject('five_y_r_squared', true)
    }
  ];

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        ...getStringObject('name'),
        Cell: ({ row }) =>
          row.original.isin === 'CASH' ? (
            row.original.name
          ) : (
            <FactSheetLink investment={row.original} />
          )
      },
      ...ratioColumns
    ],
    [investments, isLoading]
  );

  const mpColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        ...getStringObject('name')
      },
      ...ratioColumns,
      {
        accessorKey: 'type',
        header: 'Type',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div style={{ display: 'flex' }}>
            {row.original.mp_id ? 'Portfolio - ' : 'Fund'}
            {row.original.mp_id && (
              <div
                style={{
                  justifyContent: 'center',
                  fontWeight: '600',
                  color: '#4469a6',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                &nbsp; View
              </div>
            )}
          </div>
        )
      }
    ],
    [investments, isLoading]
  );

  const data = useMemo(() => funds, [funds]);

  const handleRowClick = (params: any, map = false) => {
    if (
      (params.row.original.mp_id && params.column.id === 'type') ||
      params.column.id === 'name'
    ) {
      setModalOpen(true);
      if (params.row && params.row.original.name) {
        setSelectedMpName(params.row.original.name);
      }

      if (map) {
        setParentMpName(selectedMpName);
        setParentRowData(rowData);
        setRowData(
          params.row.original.fund_allocations.map((allocation: any) => ({
            ...allocation.fund,
            allocation: allocation.allocation,
            type: 'f'
          }))
        );
        return;
      }

      setParentRowData([]);
      setRowData(params.row.original.fund_allocations);
    }
  };

  const { fundsData, mpData } = data.reduce(
    (result, row) => {
      if (!row.mp_id) {
        result.fundsData.push(row);
      } else {
        result.mpData.push(row);
      }
      return result;
    },
    { fundsData: [], mpData: [] }
  );

  const reset = (key: string) => {
    resetTableState(key);
    setCount(count + 1);
  };

  const fundsTableId = `ia_case_${selectedInvestment}_ratio_funds`;
  const mpTableId = `ia_case_${selectedInvestment}_ratio_mps`;

  return (
    <>
      {isLoading ? (
        <div className={classes.loaderWrapper}>
          <CircularProgress size="80px" />
        </div>
      ) : (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div style={{ paddingTop: '2rem' }}>
            <Grid container>
              {fundsData && fundsData.length > 0 && (
                <div style={{ width: '100%', marginBottom: '2rem' }}>
                  <h3>
                    Funds
                    <span
                      className={classes.link}
                      onClick={() => reset(`${fundsTableId}`)}
                    >
                      Reset Filters
                    </span>
                  </h3>
                  <RatioYearFilter
                    key={`${fundsTableId}_${count}`}
                    tableId={fundsTableId}
                    columns={columns.filter(
                      (row: any) => row.accessorKey !== 'allocation'
                    )}
                    data={fundsData}
                  />
                </div>
              )}
              {mpData && mpData.length > 0 && (
                <div style={{ width: '100%' }}>
                  <h3>
                    Portfolios
                    <span
                      className={classes.link}
                      onClick={() => reset(mpTableId)}
                    >
                      Reset Filters
                    </span>
                  </h3>
                  <RatioYearFilter
                    key={`${mpTableId}_${count}`}
                    tableId={mpTableId}
                    columns={mpColumns.filter(
                      (column: any) => column.accessorKey !== 'allocation'
                    )}
                    data={mpData.filter((row: any) => !row.loading)}
                    rowClick={handleRowClick}
                  />
                </div>
              )}
              <Modal
                isOpen={modalOpen}
                closeModalFunc={() => setModalOpen(false)}
              >
                <BoxWrapper
                  title={`${selectedMpName} -  Model Portfolio Allocations`}
                  closeModal={() => {
                    setModalOpen(false);
                  }}
                  style={{ width: '90vw' }}
                >
                  <div style={{ padding: '0 1rem 1rem 1rem' }}>
                    {modelPortfolioFunds.length > 0 && (
                      <>
                        <h3>Funds</h3>
                        <div>
                          <RatioYearFilter
                            key={'ratio_mpfunds'}
                            tableId="ratio_mpfunds"
                            columns={columns}
                            data={modelPortfolioFunds}
                            height="400px"
                          />
                        </div>
                      </>
                    )}
                    {nestedModelPortfolios.length > 0 && (
                      <div style={{ marginTop: '2rem' }}>
                        <h3>Portfolios</h3>
                        <div>
                          <RatioYearFilter
                            key="ratio_mp"
                            tableId="ratio_mp"
                            columns={mpColumns}
                            data={nestedModelPortfolios}
                            height="400px"
                            rowClick={(e: any) => handleRowClick(e, true)}
                          />
                        </div>
                      </div>
                    )}
                    <div style={{ margin: '1rem 0' }}>
                      <Button
                        color="primary"
                        onClick={() =>
                          parentRowData.length > 0
                            ? goBackToParentMp()
                            : setModalOpen(false)
                        }
                      >
                        {parentRowData.length > 0 ? 'Back' : 'Cancel'}
                      </Button>
                    </div>
                  </div>
                </BoxWrapper>
              </Modal>
            </Grid>
          </div>
        </LocalizationProvider>
      )}
    </>
  );
};

export default compose(
  connect(
    (state: any) => ({
      investments:
        selectors.investmentAnalyserSelectors.researchInvestments(state),
      riskRatings: selectors.investmentAnalyserSelectors.riskRatings(state),
      riskRatingsCacheIsValid:
        selectors.investmentAnalyserSelectors.riskRatingsCacheIsValid(state)
    }),
    {
      updateRiskRatings: actions.investmentAnalyserActions.updateRiskRatings
    }
  )
)(RatioTabContent);
